import React from 'react'
import {
  Card,
  Collapse,
  Descriptions,
  Empty,
  Skeleton,
  Space,
  Tag,
  Typography,
} from 'antd'

import type { PlaybackSessionDetail } from '@/types/playback'

const { Paragraph, Text, Title } = Typography

interface Props {
  session: PlaybackSessionDetail | null
  loading?: boolean
}

const renderTagList = (items: string[], color: string) => {
  if (!items.length) return <Text type="secondary">暂无</Text>
  return (
    <Space wrap>
      {items.map((item) => (
        <Tag key={item} color={color}>
          {item}
        </Tag>
      ))}
    </Space>
  )
}

const renderJsonBlock = (value: unknown) => {
  if (!value) return <Text type="secondary">暂无</Text>
  return (
    <pre
      style={{
        margin: 0,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontSize: 12,
        lineHeight: 1.6,
      }}
    >
      {JSON.stringify(value, null, 2)}
    </pre>
  )
}

const PlaybackRichDetailCard: React.FC<Props> = ({ session, loading }) => {
  if (loading) {
    return (
      <Card title="玩家回放内容" size="small">
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    )
  }

  const detail = session?.rich_detail
  if (!session || !detail) {
    return (
      <Card title="玩家回放内容" size="small">
        <Empty description="暂无玩家 rich report 内容" />
      </Card>
    )
  }

  const highlightTags = detail.highlight_tags || []
  const contrastCharm = detail.contrast_charm || []
  const soulEvolution = detail.soul_evolution as Record<string, unknown> | null
  const emotionalField = detail.emotional_field as Record<string, unknown> | null
  const analysisMetrics = detail.analysis_metrics as Record<string, unknown> | null

  return (
    <Card title="玩家回放内容" size="small">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Descriptions
          column={{ xs: 1, md: 2, xl: 4 }}
          size="small"
          bordered
          items={[
            {
              key: 'source',
              label: '内容来源',
              children: detail.source || '-',
            },
            {
              key: 'ticketing_session_code',
              label: '票务场次',
              children: detail.ticketing_session_code || '-',
            },
            {
              key: 'session_slot_uuid',
              label: 'Session Slot UUID',
              children: detail.session_slot_uuid ? <code>{detail.session_slot_uuid}</code> : '-',
            },
            {
              key: 'role',
              label: '角色名（内容）',
              children: detail.role || session.character_name || '-',
            },
          ]}
        />

        <div>
          <Space wrap align="center">
            <Title level={4} style={{ margin: 0 }}>
              {detail.generated_title || '未生成称号'}
            </Title>
            {detail.title && <Tag color="blue">{detail.title}</Tag>}
          </Space>
          {detail.subtitle && (
            <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
              {detail.subtitle}
            </Paragraph>
          )}
        </div>

        <Card size="small" title="玩家总结">
          <Paragraph style={{ marginBottom: 0 }}>
            {detail.summary || '暂无总结文案'}
          </Paragraph>
        </Card>

        <Descriptions
          column={{ xs: 1, xl: 2 }}
          size="small"
          bordered
          items={[
            {
              key: 'favorite_plot',
              label: '喜欢的剧情 / 高光剧情',
              children: detail.favorite_plot || <Text type="secondary">暂无</Text>,
            },
            {
              key: 'highlight_lines',
              label: '高光台词 / 高光描述',
              children: detail.highlight_lines || <Text type="secondary">暂无</Text>,
            },
          ]}
        />

        <Card size="small" title="成就与标签">
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div>
              <Text strong>摘要成就</Text>
              <div style={{ marginTop: 8 }}>{renderTagList(detail.achievements || [], 'gold')}</div>
            </div>
            <div>
              <Text strong>Rare Tags</Text>
              <div style={{ marginTop: 8 }}>{renderTagList(detail.rare_tags || [], 'purple')}</div>
            </div>
          </Space>
        </Card>

        {highlightTags.length > 0 && (
          <Card size="small" title={`高光片段 (${highlightTags.length})`}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {highlightTags.map((item, index) => {
                const title = String(item.title || item.name || `高光 ${index + 1}`)
                const desc = String(item.desc || item.summary || '')
                const evidence = String(item.evidence || '')
                const confidenceTier = String(item.confidenceTier || item.confidence_tier || '')
                return (
                  <Card key={`${title}-${index}`} size="small" type="inner" title={title}>
                    {desc && <Paragraph>{desc}</Paragraph>}
                    {evidence && (
                      <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                        {evidence}
                      </Paragraph>
                    )}
                    {confidenceTier && <Tag>{confidenceTier}</Tag>}
                  </Card>
                )
              })}
            </Space>
          </Card>
        )}

        {contrastCharm.length > 0 && (
          <Card size="small" title={`反差魅力 (${contrastCharm.length})`}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {contrastCharm.map((item, index) => (
                <Card
                  key={`${String(item.title || 'contrast')}-${index}`}
                  size="small"
                  type="inner"
                  title={String(item.title || `反差 ${index + 1}`)}
                >
                  <Paragraph style={{ marginBottom: 0 }}>
                    {String(item.desc || item.summary || '暂无内容')}
                  </Paragraph>
                </Card>
              ))}
            </Space>
          </Card>
        )}

        <Collapse
          size="small"
          items={[
            {
              key: 'soul_evolution',
              label: '灵魂成长 / Soul Evolution',
              children: renderJsonBlock(soulEvolution),
            },
            {
              key: 'emotional_field',
              label: '情感场 / Emotional Field',
              children: renderJsonBlock(emotionalField),
            },
            {
              key: 'analysis_metrics',
              label: '分析指标 / Analysis Metrics',
              children: renderJsonBlock(analysisMetrics),
            },
            {
              key: 'play_record_summary',
              label: 'play_record_summary',
              children: renderJsonBlock(detail.play_record_summary),
            },
          ]}
        />
      </Space>
    </Card>
  )
}

export default PlaybackRichDetailCard
